import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Animated,
  Dimensions,
  FlatList,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { contentMaxWidth, fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/lib/supabase';
import { effectiveAvatarUrl } from '@/lib/avatar';
import { getProfessionalCountsByRegion, listNationwideSponsoredForRegion, listPublicProfessionals, listUsedProfessionalRegions, listUsedProfessionCategories, type ProfessionalFacet, type PublicProfessional, type SponsoredBannerEntry } from '@/lib/professionals';
import { fetchMyRequests, countOffersOnMyRequests, getOfferCountsForMyRequests, type ServiceRequest } from '@/lib/serviceRequests';
import { VerifiedBadge } from '@/components/ui/VerifiedBadge';
import { GreeceMap } from '@/components/GreeceMap';
import { SonarPulse } from '@/components/ui/SonarPulse';
import { getNearestRegions } from '@/lib/regionNeighbors';

type Mode = 'professionals' | 'myRequests';

export default function ProfessionalsScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { user } = useAuth();
  const { profile: myProfile, loading: myProfileLoading } = useProfile();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [mode, setMode] = useState<Mode>('professionals');

  // ── Επαγγελματίες ──
  const [proRegions, setProRegions] = useState<string[]>([]);
  const [proCategories, setProCategories] = useState<{ id: number; name: string }[]>([]);
  const [proRegionFilter, setProRegionFilter] = useState<string | null>(null);
  const [proCategoryFilter, setProCategoryFilter] = useState<number | null>(null);
  const [proFacetFilter, setProFacetFilter] = useState<ProfessionalFacet | null>(null);

  const [professionals, setProfessionals] = useState<PublicProfessional[]>([]);
  const [professionalsLoading, setProfessionalsLoading] = useState(true);
  const [regionCounts, setRegionCounts] = useState<Record<string, number>>({});
  const [showMap, setShowMap] = useState(false);
  const mapBtnShake = useRef(new Animated.Value(0)).current;
  const showResultsBounce = useRef(new Animated.Value(0)).current;
  const [sonarNote, setSonarNote] = useState<string | null>(null);
  const sonarScanDone = useRef(false);
  const [introSonarActive, setIntroSonarActive] = useState(true);
  const [showIntroModal, setShowIntroModal] = useState(false);

  useEffect(() => {
    void listUsedProfessionalRegions().then(setProRegions).catch(() => setProRegions([]));
    void listUsedProfessionCategories().then(setProCategories).catch(() => setProCategories([]));
  }, []);

  // ── Κυλιόμενο banner nationwide sponsors (20 μελάνια/ημέρα) — μόνο του
  // νομού που βλέπει ο χρήστης, γυρίζει κάθε 3s, χειροκίνητο swipe διαθέσιμο. ──
  const bannerRegion = proRegionFilter ?? myProfile?.region ?? null;
  const [bannerEntries, setBannerEntries] = useState<SponsoredBannerEntry[]>([]);
  const [bannerIndex, setBannerIndex] = useState(0);
  const bannerListRef = useRef<FlatList>(null);
  const bannerTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const bannerWidth = Dimensions.get('window').width - spacing.lg * 2;

  useEffect(() => {
    if (!bannerRegion) { setBannerEntries([]); return; }
    void listNationwideSponsoredForRegion(bannerRegion).then(setBannerEntries).catch(() => setBannerEntries([]));
  }, [bannerRegion]);

  useEffect(() => {
    if (bannerTimer.current) clearInterval(bannerTimer.current);
    if (bannerEntries.length <= 1) return;
    bannerTimer.current = setInterval(() => {
      setBannerIndex((i) => {
        const next = (i + 1) % bannerEntries.length;
        bannerListRef.current?.scrollToOffset({ offset: next * bannerWidth, animated: true });
        return next;
      });
    }, 3000);
    return () => { if (bannerTimer.current) clearInterval(bannerTimer.current); };
  }, [bannerEntries.length, bannerWidth]);

  function onBannerScrollEnd(offsetX: number) {
    const idx = Math.round(offsetX / bannerWidth);
    setBannerIndex(Math.max(0, Math.min(idx, bannerEntries.length - 1)));
  }

  useEffect(() => {
    void AsyncStorage.getItem('professionals_intro_shown').then((shown) => {
      if (!shown) setShowIntroModal(true);
    });
  }, []);

  function dismissIntroModal() {
    setShowIntroModal(false);
    void AsyncStorage.setItem('professionals_intro_shown', '1');
  }

  useEffect(() => {
    if (!professionalsLoading && introSonarActive) {
      const timer = setTimeout(() => setIntroSonarActive(false), 400);
      return () => clearTimeout(timer);
    }
  }, [professionalsLoading, introSonarActive]);

  // ── "Sonar scan": αυτόματα προτείνει τον νομό του χρήστη, και αν δεν έχει
  // επαγγελματίες εκεί, σκανάρει προς τους πλησιέστερους νομούς. ──
  useEffect(() => {
    if (sonarScanDone.current) return;
    if (proRegionFilter !== null) { sonarScanDone.current = true; return; }
    if (Object.keys(regionCounts).length === 0) return;
    if (myProfileLoading) return;
    const myRegion = myProfile?.region;
    if (!myRegion) { sonarScanDone.current = true; return; }
    sonarScanDone.current = true;
    if ((regionCounts[myRegion] ?? 0) > 0) {
      setProRegionFilter(myRegion);
      return;
    }
    const neighbor = getNearestRegions(myRegion, 8).find((r) => (regionCounts[r] ?? 0) > 0);
    if (neighbor) {
      setProRegionFilter(neighbor);
      setSonarNote(t('circle.sonar_nearestRegion', { region: neighbor }));
    } else {
      setSonarNote(t('circle.sonar_noneNearby'));
    }
  }, [regionCounts, myProfile?.region, myProfileLoading, proRegionFilter, t]);

  function selectRegion(region: string | null) {
    setSonarNote(null);
    setProRegionFilter(region);
  }

  useEffect(() => {
    void getProfessionalCountsByRegion(proFacetFilter, proCategoryFilter).then(setRegionCounts).catch(() => setRegionCounts({}));
  }, [proFacetFilter, proCategoryFilter]);

  useEffect(() => {
    setProfessionalsLoading(true);
    listPublicProfessionals(proRegionFilter, proCategoryFilter, proFacetFilter)
      .then(setProfessionals)
      .catch(() => setProfessionals([]))
      .finally(() => setProfessionalsLoading(false));
  }, [proRegionFilter, proCategoryFilter, proFacetFilter]);

  // Διακριτικό shake στο κουμπί χάρτη όταν είναι ανενεργός, ώστε να τραβήξει την προσοχή.
  useEffect(() => {
    if (mode !== 'professionals' || showMap) return;
    const shake = Animated.loop(
      Animated.sequence([
        Animated.delay(2200),
        Animated.sequence([-1, 1, -1, 1, 0].map((to) =>
          Animated.timing(mapBtnShake, { toValue: to, duration: 60, useNativeDriver: Platform.OS !== 'web' }),
        )),
      ]),
    );
    shake.start();
    return () => { shake.stop(); mapBtnShake.setValue(0); };
  }, [mode, showMap, mapBtnShake]);

  // Bounce στο κουμπί "Εμφάνιση" ανά 3 δευτερόλεπτα, όσο ο χάρτης είναι ανοιχτός.
  useEffect(() => {
    if (mode !== 'professionals' || !showMap) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(3000),
        Animated.timing(showResultsBounce, { toValue: 1, duration: 200, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(showResultsBounce, { toValue: 0, duration: 200, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    loop.start();
    return () => { loop.stop(); showResultsBounce.setValue(0); };
  }, [mode, showMap, showResultsBounce]);

  // ── Τα δικά μου αιτήματα προσφοράς ──
  const [myRequests, setMyRequests] = useState<ServiceRequest[]>([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(true);
  const [requestCategories, setRequestCategories] = useState<{ id: number; name: string }[]>([]);
  const [offerCountsForMyRequests, setOfferCountsForMyRequests] = useState<Record<string, number>>({});
  const [newOffersOnMyRequestsCount, setNewOffersOnMyRequestsCount] = useState(0);

  useEffect(() => {
    void supabase.from('profession_categories').select('id, name').order('name')
      .then(({ data }) => { if (data) setRequestCategories(data as { id: number; name: string }[]); });
  }, []);

  const requestCategoryName = (id: number | null) => requestCategories.find((c) => c.id === id)?.name ?? null;

  useFocusEffect(useCallback(() => {
    if (mode !== 'myRequests' || !user) return;
    setMyRequestsLoading(true);
    void fetchMyRequests(user.id).then(setMyRequests).catch(() => setMyRequests([])).finally(() => setMyRequestsLoading(false));
    void (async () => {
      try {
        await AsyncStorage.setItem('my_requests_offers_last_seen', new Date().toISOString());
        setNewOffersOnMyRequestsCount(0);
        setOfferCountsForMyRequests(await getOfferCountsForMyRequests());
      } catch { /* αγνόησε */ }
    })();
  }, [mode, user]));

  useFocusEffect(useCallback(() => {
    void (async () => {
      try {
        const lastSeen = await AsyncStorage.getItem('my_requests_offers_last_seen');
        setNewOffersOnMyRequestsCount(await countOffersOnMyRequests(lastSeen));
      } catch { setNewOffersOnMyRequestsCount(0); }
    })();
  }, []));

  return (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View style={styles.headerTitle}>
          <Ionicons name="briefcase-outline" size={20} color={colors.text} />
          <Text style={styles.headerText}>{t('circle.filter_professionals')}</Text>
        </View>
        {mode === 'professionals' ? (
          <View style={styles.spacer} />
        ) : (
          <Pressable
            onPress={() => router.push({ pathname: '/(app)/requests/new', params: { targetProfessionalId: '', targetProfessionalName: '', presetTitle: '', editId: '' } } as any)}
            hitSlop={10}
            accessibilityRole="button"
          >
            <Ionicons name="add-circle-outline" size={24} color={colors.primary} />
          </Pressable>
        )}
      </View>

      {/* Mode switcher */}
      <View style={styles.modeTabs}>
        <Pressable
          style={[styles.modeTab, mode === 'professionals' && styles.modeTabActive]}
          onPress={() => setMode('professionals')}
          accessibilityRole="tab"
        >
          <Text style={[styles.modeTabText, mode === 'professionals' && styles.modeTabTextActive]}>
            {t('professionals.tab_browse')}
          </Text>
        </Pressable>
        <Pressable
          style={[styles.modeTab, mode === 'myRequests' && styles.modeTabActive]}
          onPress={() => setMode('myRequests')}
          accessibilityRole="tab"
        >
          <Text style={[styles.modeTabText, mode === 'myRequests' && styles.modeTabTextActive]}>
            {t('professionals.tab_requests')}
          </Text>
          {newOffersOnMyRequestsCount > 0 && (
            <View style={styles.modeTabBadge}>
              <Text style={styles.modeTabBadgeText}>
                {newOffersOnMyRequestsCount > 9 ? '9+' : newOffersOnMyRequestsCount}
              </Text>
            </View>
          )}
        </Pressable>
      </View>

      {mode === 'professionals' ? (
        <>
          {introSonarActive && (
            <View style={styles.introSonarBanner}>
              <View style={styles.introSonarIconWrap}>
                <SonarPulse active size={70} />
                <Ionicons name="radio-outline" size={22} color={colors.primary} />
              </View>
              <Text style={styles.introSonarText}>{t('circle.sonar_scanning')}</Text>
            </View>
          )}

          <View style={styles.proNotice}>
            <Ionicons name="information-circle-outline" size={14} color={colors.textMuted} />
            <Text style={styles.proNoticeText}>{t('circle.pro_notice')}</Text>
          </View>

          {!!sonarNote && (
            <View style={styles.sonarNote}>
              <Ionicons name="radio-outline" size={14} color={colors.primary} />
              <Text style={styles.sonarNoteText}>{sonarNote}</Text>
              <Pressable onPress={() => selectRegion(null)} hitSlop={6}>
                <Text style={styles.sonarNoteAction}>{t('circle.sonar_seeAll')}</Text>
              </Pressable>
              <Pressable onPress={() => setShowMap(true)} hitSlop={6}>
                <Text style={styles.sonarNoteAction}>{t('circle.sonar_openMap')}</Text>
              </Pressable>
            </View>
          )}

          <ScrollView
            horizontal
            style={styles.facetScroll}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.facetRow}
          >
            {([
              ['services', t('circle.facet_services')],
              ['eshop', t('circle.facet_eshop')],
              ['physical_only', t('circle.facet_physical_only')],
            ] as [ProfessionalFacet, string][]).map(([facet, label]) => (
              <Pressable
                key={facet}
                style={[styles.facetChip, proFacetFilter === facet && styles.facetChipActive]}
                onPress={() => setProFacetFilter(proFacetFilter === facet ? null : facet)}
                accessibilityRole="button"
              >
                <Text style={[styles.facetChipText, proFacetFilter === facet && styles.facetChipTextActive]}>{label}</Text>
              </Pressable>
            ))}
          </ScrollView>

          {showMap && (
            <View style={styles.mapSection}>
              <GreeceMap
                countsByRegion={regionCounts}
                selectedRegion={proRegionFilter}
                onSelectRegion={selectRegion}
              />
              {proRegionFilter && (
                <Pressable style={styles.mapSelectedChip} onPress={() => selectRegion(null)} accessibilityRole="button">
                  <Ionicons name="location" size={13} color={colors.primary} />
                  <Text style={styles.mapSelectedChipText}>{proRegionFilter}</Text>
                  <Ionicons name="close-circle" size={15} color={colors.textMuted} />
                </Pressable>
              )}

              {proCategories.length > 0 && (
                <View style={styles.inlineCategorySection}>
                  <Text style={styles.proFilterSectionLabel}>{t('circle.filter_profession')}</Text>
                  <View style={styles.proFilterChipsWrap}>
                    <Pressable
                      style={[styles.proFilterChip, proCategoryFilter === null && styles.proFilterChipActive]}
                      onPress={() => setProCategoryFilter(null)}
                    >
                      <Text style={[styles.proFilterChipText, proCategoryFilter === null && styles.proFilterChipTextActive]}>{t('circle.filter_professionAll')}</Text>
                    </Pressable>
                    {proCategories.map((cat) => (
                      <Pressable
                        key={cat.id}
                        style={[styles.proFilterChip, proCategoryFilter === cat.id && styles.proFilterChipActive]}
                        onPress={() => setProCategoryFilter(proCategoryFilter === cat.id ? null : cat.id)}
                      >
                        <Text style={[styles.proFilterChipText, proCategoryFilter === cat.id && styles.proFilterChipTextActive]}>{cat.name}</Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}
            </View>
          )}

          {!showMap && bannerEntries.length > 0 && (
            <View style={styles.sponsorBannerWrap}>
              <FlatList
                ref={bannerListRef}
                data={bannerEntries}
                keyExtractor={(p) => p.id}
                horizontal
                pagingEnabled
                showsHorizontalScrollIndicator={false}
                onScrollBeginDrag={() => { if (bannerTimer.current) clearInterval(bannerTimer.current); }}
                onMomentumScrollEnd={(e) => onBannerScrollEnd(e.nativeEvent.contentOffset.x)}
                renderItem={({ item: p }) => {
                  const displayName = (p.show_business_name && p.business_name) ? p.business_name : (p.full_name ?? '—');
                  const logoUri = (p.show_business_name && p.business_logo_url) ? p.business_logo_url : effectiveAvatarUrl(p);
                  return (
                    <Pressable
                      style={[styles.sponsorBannerCard, { width: bannerWidth }]}
                      onPress={() => router.push({ pathname: '/(app)/users/portfolio/[id]', params: { id: p.id } })}
                      accessibilityRole="button"
                    >
                      {logoUri ? (
                        <Image source={{ uri: logoUri }} style={styles.sponsorBannerLogo} />
                      ) : (
                        <View style={[styles.sponsorBannerLogo, styles.feedAvatarFallback]}>
                          <Text style={styles.feedAvatarLetter}>{displayName?.[0]?.toUpperCase() ?? '?'}</Text>
                        </View>
                      )}
                      <View style={styles.sponsorBannerInfo}>
                        <View style={styles.sponsorBannerBadge}>
                          <Ionicons name="megaphone" size={9} color={colors.primary} />
                          <Text style={styles.sponsorBannerBadgeText}>{t('circle.pro_sponsored')}</Text>
                        </View>
                        <Text style={styles.sponsorBannerName} numberOfLines={1}>{displayName}</Text>
                        {!!p.profession_category_name && (
                          <Text style={styles.sponsorBannerMeta} numberOfLines={1}>{p.profession_category_name}</Text>
                        )}
                        {p.avg_rating != null && (
                          <Text style={styles.sponsorBannerMeta}>★ {p.avg_rating.toFixed(1)}</Text>
                        )}
                      </View>
                    </Pressable>
                  );
                }}
              />
              {bannerEntries.length > 1 && (
                <View style={styles.sponsorBannerDots}>
                  {bannerEntries.map((_, i) => (
                    <View key={i} style={[styles.sponsorBannerDot, i === bannerIndex && styles.sponsorBannerDotActive]} />
                  ))}
                </View>
              )}
            </View>
          )}

          {professionalsLoading ? (
            <View style={styles.feedEmpty}>
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : showMap ? (
            <View style={styles.feedEmpty}>
              <Ionicons name={professionals.length === 0 ? 'briefcase-outline' : 'checkmark-circle-outline'} size={32} color={colors.border} />
              <Text style={styles.feedEmptyText}>
                {professionals.length === 0 ? t('circle.pro_empty') : t('circle.pro_resultsCount', { count: professionals.length })}
              </Text>
            </View>
          ) : professionals.length === 0 ? (
            <View style={styles.feedEmpty}>
              <Ionicons name="briefcase-outline" size={40} color={colors.border} />
              <Text style={styles.feedEmptyText}>{t('circle.pro_empty')}</Text>
            </View>
          ) : (
            <FlatList
              style={styles.tabFlatList}
              data={professionals}
              keyExtractor={(p) => p.id}
              contentContainerStyle={styles.feedList}
              showsVerticalScrollIndicator={false}
              renderItem={({ item: p }) => {
                const displayName = (p.show_business_name && p.business_name) ? p.business_name : (p.full_name ?? '—');
                const logoUri = (p.show_business_name && p.business_logo_url) ? p.business_logo_url : effectiveAvatarUrl(p);
                return (
                  <Pressable
                    style={[styles.proCard, p.is_sponsored && styles.proCardSponsored]}
                    onPress={() => router.push({ pathname: '/(app)/users/portfolio/[id]', params: { id: p.id } })}
                    accessibilityRole="button"
                  >
                    {!!p.is_sponsored && (
                      <View style={styles.proSponsoredBadge}>
                        <Ionicons name="megaphone" size={9} color={colors.primary} />
                        <Text style={styles.proSponsoredBadgeText}>{t('circle.pro_sponsored')}</Text>
                      </View>
                    )}
                    {logoUri ? (
                      <Image source={{ uri: logoUri }} style={styles.proAvatar} />
                    ) : (
                      <View style={[styles.proAvatar, styles.feedAvatarFallback]}>
                        <Text style={styles.feedAvatarLetter}>{displayName?.[0]?.toUpperCase() ?? '?'}</Text>
                      </View>
                    )}
                    <View style={styles.proInfo}>
                      <View style={styles.proNameRow}>
                        <Text style={styles.proName} numberOfLines={1}>{displayName}</Text>
                        {!!p.verified && <VerifiedBadge compact />}
                      </View>
                      {!!p.specialty && <Text style={styles.proSpecialty} numberOfLines={1}>{p.specialty}</Text>}
                      <View style={styles.proMetaRow}>
                        {p.avg_rating != null && (
                          <Text style={styles.proRating}>★ {p.avg_rating.toFixed(1)}</Text>
                        )}
                        {!!p.completed_count && (
                          <Text style={styles.proMetaText}>{t('circle.pro_completedCount', { count: p.completed_count })}</Text>
                        )}
                        {!!p.location_name && (
                          <Text style={styles.proMetaText}>📍 {p.location_name}</Text>
                        )}
                      </View>
                    </View>
                    <Ionicons name="chevron-forward" size={18} color={colors.textMuted} />
                  </Pressable>
                );
              }}
            />
          )}

          {(proRegions.length > 0 || proCategories.length > 0) && (showMap ? (
            <Animated.View
              style={{
                position: 'absolute', right: spacing.lg, bottom: spacing.lg,
                transform: [{ scale: showResultsBounce.interpolate({ inputRange: [0, 1], outputRange: [1, 1.15] }) }],
              }}
            >
              <Pressable
                style={styles.showResultsPillInner}
                onPress={() => setShowMap(false)}
                accessibilityRole="button"
              >
                <Ionicons name="checkmark-circle" size={18} color={colors.textOnPrimary} />
                <Text style={styles.showResultsPillText}>{t('circle.showResults')}</Text>
              </Pressable>
            </Animated.View>
          ) : (
            <Pressable
              style={[styles.proFab, (proRegionFilter !== null || proCategoryFilter !== null) && styles.proFabActive]}
              onPress={() => setShowMap((v) => !v)}
              accessibilityRole="button"
              accessibilityLabel={t('circle.filtersRegion')}
            >
              <SonarPulse active={professionalsLoading} size={56} />
              <Animated.View
                style={{
                  transform: [{
                    rotate: mapBtnShake.interpolate({ inputRange: [-1, 1], outputRange: ['-18deg', '18deg'] }),
                  }],
                }}
              >
                <Ionicons
                  name="map-outline"
                  size={22}
                  color={(proRegionFilter !== null || proCategoryFilter !== null) ? '#fff' : colors.primary}
                />
              </Animated.View>
              <Text
                numberOfLines={2}
                style={[
                  styles.proFabText,
                  (proRegionFilter !== null || proCategoryFilter !== null) && styles.proFabTextActive,
                ]}
              >
                {t('circle.filtersRegion')}
              </Text>
              {(proRegionFilter !== null || proCategoryFilter !== null) && <View style={styles.proFabDot} />}
            </Pressable>
          ))}
        </>
      ) : (
        <>
          <Text style={styles.requestsSubtitle}>{t('requests.subtitle')}</Text>

          {myRequestsLoading ? (
            <View style={styles.feedEmpty}>
              <ActivityIndicator color={colors.primary} />
            </View>
          ) : myRequests.length === 0 ? (
            <View style={styles.feedEmpty}>
              <Ionicons name="megaphone-outline" size={40} color={colors.border} />
              <Text style={styles.feedEmptyText}>{t('requests.emptyMine')}</Text>
            </View>
          ) : (
            <ScrollView contentContainerStyle={[styles.requestsList, { paddingBottom: insets.bottom + spacing.xl }]}>
              {myRequests.map((r) => (
                <Pressable
                  key={r.id}
                  style={styles.requestCard}
                  onPress={() => router.push({ pathname: '/(app)/requests/[id]', params: { id: r.id } } as any)}
                  accessibilityRole="button"
                >
                  {r.status === 'open' && !!offerCountsForMyRequests[r.id] && (
                    <View style={styles.requestOfferBadge}>
                      <Text style={styles.requestOfferBadgeText}>
                        {offerCountsForMyRequests[r.id] > 9 ? '9+' : offerCountsForMyRequests[r.id]}
                      </Text>
                    </View>
                  )}
                  <View style={styles.requestCardTop}>
                    <Text style={styles.requestCardTitle} numberOfLines={1}>{r.title}</Text>
                    {r.status !== 'open' && (
                      <View style={styles.statusBadge}>
                        <Text style={styles.statusBadgeText}>
                          {r.status === 'closed' ? t('requests.status_closed') : t('requests.status_cancelled')}
                        </Text>
                      </View>
                    )}
                  </View>
                  {!!r.description && <Text style={styles.requestCardDesc} numberOfLines={2}>{r.description}</Text>}
                  {!!requestCategoryName(r.profession_category_id) && (
                    <View style={styles.categoryChip}>
                      <Text style={styles.categoryChipText}>{requestCategoryName(r.profession_category_id)}</Text>
                    </View>
                  )}
                </Pressable>
              ))}
            </ScrollView>
          )}
        </>
      )}

      <Modal visible={showIntroModal} transparent animationType="fade" onRequestClose={dismissIntroModal}>
        <Pressable style={styles.introModalOverlay} onPress={dismissIntroModal} accessibilityRole="button">
          <Pressable style={styles.introModalCard} onPress={() => {}}>
            <Text style={styles.introModalTitle}>{t('circle.intro_title')}</Text>

            <View style={styles.introModalRow}>
              <View style={styles.introModalIconWrap}>
                <Ionicons name="map-outline" size={16} color={colors.primary} />
              </View>
              <Text style={styles.introModalText}>{t('circle.intro_map')}</Text>
            </View>
            <View style={styles.introModalRow}>
              <View style={styles.introModalIconWrap}>
                <Ionicons name="options-outline" size={16} color={colors.primary} />
              </View>
              <Text style={styles.introModalText}>{t('circle.intro_facets')}</Text>
            </View>
            <View style={styles.introModalRow}>
              <View style={styles.introModalIconWrap}>
                <Ionicons name="radio-outline" size={16} color={colors.primary} />
              </View>
              <Text style={styles.introModalText}>{t('circle.intro_sonar')}</Text>
            </View>
            <View style={styles.introModalRow}>
              <View style={styles.introModalIconWrap}>
                <Ionicons name="briefcase-outline" size={16} color={colors.primary} />
              </View>
              <Text style={styles.introModalText}>{t('circle.intro_requests')}</Text>
            </View>

            <Pressable style={styles.introModalClose} onPress={dismissIntroModal} accessibilityRole="button">
              <Text style={styles.introModalCloseText}>{t('circle.intro_gotIt')}</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
    },
    headerTitle: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    spacer: { width: 24 },
    headerText: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    modeTabs: {
      flexDirection: 'row', marginHorizontal: spacing.lg,
      borderRadius: radius.md, borderWidth: 1, borderColor: colors.border,
      overflow: 'hidden', marginBottom: spacing.sm,
    },
    modeTab: { flex: 1, flexDirection: 'row', paddingVertical: 10, alignItems: 'center', justifyContent: 'center', gap: 6 },
    modeTabActive: { backgroundColor: colors.primary },
    modeTabText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    modeTabTextActive: { color: colors.textOnPrimary },
    modeTabBadge: {
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: '#EF4444',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
    },
    modeTabBadgeText: { color: '#fff', fontSize: 10, fontWeight: '800' },
    tabFlatList: { flex: 1 },
    feedList: {
      // Σε πλατιά οθόνη το περιεχόμενο μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      padding: spacing.md,
      gap: spacing.md,
    },
    feedEmpty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      padding: spacing.xl,
      paddingBottom: 90,
    },
    feedEmptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    proNotice: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.sm,
    },
    introSonarBanner: {
      alignItems: 'center',
      gap: spacing.xs,
      paddingTop: spacing.md,
      paddingBottom: spacing.xs,
    },
    introSonarIconWrap: {
      width: 70,
      height: 70,
      alignItems: 'center',
      justifyContent: 'center',
    },
    introSonarText: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      fontWeight: '600',
    },
    introModalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.55)',
      justifyContent: 'center',
      padding: spacing.lg,
    },
    introModalCard: {
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.lg,
      gap: spacing.md,
      ...shadow.card,
    },
    introModalTitle: {
      color: colors.text,
      fontSize: fontSize.lg,
      fontWeight: '800',
      textAlign: 'center',
    },
    introModalRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.sm,
    },
    introModalIconWrap: {
      width: 32,
      height: 32,
      borderRadius: 16,
      backgroundColor: `${colors.primary}18`,
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    introModalText: {
      flex: 1,
      color: colors.textMuted,
      fontSize: fontSize.sm,
      lineHeight: 19,
    },
    introModalClose: {
      backgroundColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 12,
      alignItems: 'center',
      marginTop: spacing.xs,
    },
    introModalCloseText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.md,
      fontWeight: '800',
    },
    sonarNote: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: spacing.xs,
      backgroundColor: `${colors.primary}14`,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: `${colors.primary}40`,
      borderRadius: radius.md,
      marginHorizontal: spacing.lg,
      marginBottom: spacing.sm,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
    },
    sonarNoteText: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      flexShrink: 1,
      flexGrow: 1,
      minWidth: 120,
    },
    sonarNoteAction: {
      color: colors.primary,
      fontSize: fontSize.xs,
      fontWeight: '700',
    },
    proNoticeText: {
      color: colors.textMuted,
      fontSize: fontSize.xs,
      flex: 1,
    },
    mapSection: {
      paddingHorizontal: spacing.md,
      paddingBottom: spacing.sm,
      alignItems: 'center',
      gap: spacing.xs,
    },
    mapSelectedChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: `${colors.primary}18`,
      borderRadius: radius.full,
      paddingHorizontal: spacing.sm,
      paddingVertical: 5,
    },
    mapSelectedChipText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    facetScroll: {
      flexGrow: 0,
      flexShrink: 0,
    },
    facetRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.sm,
    },
    facetChip: {
      paddingHorizontal: spacing.md,
      paddingVertical: 7,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
    },
    facetChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    facetChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    facetChipTextActive: { color: '#0D1B2A' },
    proFilterChip: {
      paddingHorizontal: spacing.sm,
      paddingVertical: 6,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
    },
    proFilterChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    proFilterChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    proFilterChipTextActive: { color: '#fff' },
    proFab: {
      position: 'absolute',
      right: spacing.lg,
      bottom: spacing.lg,
      width: 76,
      height: 76,
      borderRadius: 20,
      backgroundColor: colors.surface,
      borderWidth: 1.5,
      borderColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
      gap: 2,
      ...shadow.card,
    },
    proFabActive: { backgroundColor: colors.primary },
    proFabText: {
      color: colors.primary,
      fontSize: 10,
      fontWeight: '700',
      textAlign: 'center',
      lineHeight: 12,
    },
    proFabTextActive: { color: '#fff' },
    proFabDot: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: '#fff',
    },
    showResultsPillInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 12,
      paddingHorizontal: spacing.lg,
      borderRadius: radius.full,
      backgroundColor: colors.primary,
      ...shadow.card,
    },
    showResultsPillText: {
      color: colors.textOnPrimary,
      fontSize: fontSize.sm,
      fontWeight: '800',
    },
    inlineCategorySection: {
      width: '100%',
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    proFilterSectionLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700', marginBottom: spacing.xs },
    proFilterChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    proCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.md,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
    },
    proCardSponsored: { position: 'relative', borderColor: colors.primary, borderWidth: 1 },
    proSponsoredBadge: {
      position: 'absolute',
      top: -8,
      right: spacing.md,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 2,
      paddingHorizontal: 7,
    },
    proSponsoredBadgeText: { color: colors.primary, fontSize: 9.5, fontWeight: '700' },
    sponsorBannerWrap: { marginBottom: spacing.sm },
    sponsorBannerCard: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      backgroundColor: colors.surface,
      borderRadius: radius.lg,
      padding: spacing.md,
      borderWidth: 1,
      borderColor: colors.primary,
    },
    sponsorBannerLogo: { width: 52, height: 52, borderRadius: 26 },
    sponsorBannerInfo: { flex: 1, gap: 2 },
    sponsorBannerBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      alignSelf: 'flex-start',
      backgroundColor: colors.background,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radius.full,
      paddingVertical: 1,
      paddingHorizontal: 6,
      marginBottom: 1,
    },
    sponsorBannerBadgeText: { color: colors.primary, fontSize: 9, fontWeight: '700' },
    sponsorBannerName: { color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    sponsorBannerMeta: { color: colors.textMuted, fontSize: fontSize.xs },
    sponsorBannerDots: { flexDirection: 'row', justifyContent: 'center', gap: 5, marginTop: spacing.xs },
    sponsorBannerDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: colors.border },
    sponsorBannerDotActive: { backgroundColor: colors.primary },
    proAvatar: { width: 48, height: 48, borderRadius: 24 },
    proInfo: { flex: 1, gap: 2 },
    proNameRow: { flexDirection: 'row', alignItems: 'center', gap: 4 },
    proName: { color: colors.text, fontSize: fontSize.md, fontWeight: '700', flexShrink: 1 },
    proSpecialty: { color: colors.textMuted, fontSize: fontSize.sm },
    proMetaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: 2 },
    proRating: { color: colors.accent, fontSize: fontSize.sm, fontWeight: '700' },
    proMetaText: { color: colors.textMuted, fontSize: 11 },
    feedAvatarFallback: {
      backgroundColor: colors.surfaceAlt,
      alignItems: 'center',
      justifyContent: 'center',
    },
    feedAvatarLetter: {
      color: colors.textMuted,
      fontSize: 18,
      fontWeight: '700',
    },
    requestsSubtitle: { color: colors.textMuted, fontSize: fontSize.sm, paddingHorizontal: spacing.lg, marginBottom: spacing.sm },
    requestsList: {
      // Σε πλατιά οθόνη το περιεχόμενο μένει σε αναγνώσιμο πλάτος αντί να
      // τεντώνεται· σε κινητό το maxWidth δεν ενεργοποιείται ποτέ.
      width: '100%',
      maxWidth: contentMaxWidth.wide,
      alignSelf: 'center',
      paddingHorizontal: spacing.lg,
      gap: spacing.sm,
    },
    requestCard: {
      backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1,
      borderColor: colors.border, padding: spacing.md, gap: 6, ...shadow.card,
      position: 'relative',
    },
    requestOfferBadge: {
      position: 'absolute', top: spacing.sm, right: spacing.sm,
      minWidth: 20, height: 20, borderRadius: 10, backgroundColor: '#EF4444',
      alignItems: 'center', justifyContent: 'center', paddingHorizontal: 5,
    },
    requestOfferBadgeText: { color: '#fff', fontSize: 11, fontWeight: '800' },
    requestCardTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    requestCardTitle: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    requestCardDesc: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 19 },
    statusBadge: { backgroundColor: colors.surfaceAlt, borderRadius: radius.full, paddingHorizontal: 8, paddingVertical: 3 },
    statusBadgeText: { color: colors.textMuted, fontSize: 11, fontWeight: '700' },
    categoryChip: {
      alignSelf: 'flex-start', backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full, paddingVertical: 3, paddingHorizontal: 10,
    },
    categoryChipText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '600' },
  });
}
